import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import BasicTable from '../molecules/BasicTable';
import orderBy from 'lodash/orderBy';
import Box from '@mui/material/Box';
import { Button, SingleLineText, TypeInput, Checkbox, ValidationTextField, SelectField } from '@bloomays-lib/ui.shared';
import { styled } from '@mui/material/styles';
import { ActivityDayDetails } from '@bloomays-lib/types.shared';
import { useEffect, useMemo, useState } from 'react';
import { generateOptionsCRA } from '../../helpers/CRA';
import { Theme } from '@mui/material/styles';
import { Logger } from '../../services/serviceLogger';
import debounce from 'lodash/debounce';
const logger = Logger('FormCRA');

type FormCRAProps = {
  eachDays: ActivityDayDetails[];
  onCRAUpdate: (activityDayDetailss: ActivityDayDetails[]) => void;
  hoursPerDay: number;
  readOnly?: boolean;
  fullWidth?: boolean;
};

export const FormCRA = ({ eachDays, onCRAUpdate, hoursPerDay, readOnly = false }: FormCRAProps) => {
  const [showAstreinteSelect, setShowAstreinteSelect] = useState<{ [key: number]: boolean }>({});
  const [showApplyValue, setShowApplyValue] = useState(false);
  const [valueOfAll, setValueOfAll] = useState(0);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [comments, setComments] = useState<{ [key: number]: string }>(() => {
    const commentsMap: { [key: number]: string } = {};
    eachDays.forEach((day) => {
      if (day.comment) {
        commentsMap[day.id] = day.comment;
      }
    });
    return commentsMap;
  });
  const debouncedSetComments = debounce(setComments, 10);

  useEffect(() => {
    eachDays.forEach((day) => {
      if (day.comment) {
        comments[day.id] = day.comment;
      } else {
        delete comments[day.id];
      }
    });
    setComments(comments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eachDays]);

  useEffect(() => {
    eachDays.forEach((day) => {
      if (comments[day.id]) {
        day.comment = comments[day.id];
      }
    });
    logger.debug('debouncedComments onCRAUpdate', comments);
    onCRAUpdate(eachDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, onCRAUpdate]);

  const getShowValueFromEachDays = (days: ActivityDayDetails[]) => {
    const initValueShowAstreinte: { [key: number]: boolean } = {};
    days.forEach((td) => {
      if (td.astreinte && td.astreinte > 0) {
        initValueShowAstreinte[td.id] = true;
      }
    });
    return initValueShowAstreinte;
  };

  const hour = useMemo(() => {
    const hours = generateOptionsCRA(hoursPerDay);
    return hours.map((h) => {
      if (h.value === 1) {
        h.label = '1 journée';
      }
      if (h.value === 0.5) {
        h.label = '1/2 journée';
      }
      return h;
    });
  }, [hoursPerDay]);

  const orderedDays = useMemo(() => {
    return orderBy(eachDays, ['id'], ['asc']);
  }, [eachDays]);

  const hourHNO = useMemo(() => {
    return generateOptionsCRA(hoursPerDay || 12);
  }, [hoursPerDay]);

  const SelectAll = () => {
    return (
      <>
        <Checkbox
          id="selectAll"
          checked={isSelectAll}
          name={'selectAll'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setShowApplyValue(e.target.checked);
            setIsSelectAll(e.target.checked);
            selectAll(e.target.checked);
          }}
        />
        <SingleLineText text={'Tout selectionner'} />
        <SingleLineText text={'(hors week-ends et jours fériés)'} />
      </>
    ) as React.ReactNode;
  };

  useEffect(() => {
    const result = getShowValueFromEachDays(eachDays);
    setShowAstreinteSelect(result);
  }, [eachDays]);

  const handleHalfDay = (id: number, nb: number) => {
    const updatedTableData = eachDays.map((item) => {
      if (item.id === id) {
        return { ...item, dayWorked: nb };
      }
      return item;
    });
    onCRAUpdate(updatedTableData);
  };

  const handleShowAstreinte = (id: number, show: boolean, astreinte?: number) => {
    setShowAstreinteSelect((prev) => {
      return { ...prev, [id]: show };
    });
  };

  const handleAstreinte = (id: number, astreinte: number) => {
    const updatedTableData = eachDays.map((item) => {
      if (item.id === id) {
        return { ...item, astreinte: astreinte };
      }
      return item;
    });
    onCRAUpdate(updatedTableData);
  };

  const selectAll = (isSelect: boolean) => {
    const updatedTableData = eachDays.map((item) => {
      const isWeekEndOrHoliday = item.day === 'Samedi' || item.day === 'Dimanche' || item.isHoliday;
      return { ...item, checked: isWeekEndOrHoliday ? false : isSelect };
    });
    onCRAUpdate(updatedTableData);
  };

  const handleWorkedDay = (id: number, checked: boolean) => {
    const updatedTableData = eachDays.map((item) => {
      if (item.id === id) {
        return { ...item, checked: checked };
      }
      return item;
    });
    onCRAUpdate(updatedTableData);
  };

  const handleComment = (id: number, comment: string) => {
    // Mise à jour immédiate pour l'affichage
    setComments((prev) => ({
      ...prev,
      [id]: comment,
    }));

    // Sauvegarde avec débounce
    debouncedSetComments((prev) => {
      prev[id] = comment;
      return prev;
    });
    logger.debug('handleComment', comments);
  };

  const applyValue = (value: number) => {
    const updatedTableData = eachDays.map((item) => {
      if (item.checked) {
        return { ...item, dayWorked: value };
      }
      return item;
    });
    onCRAUpdate(updatedTableData);
  };

  return (
    <>
      {showApplyValue && (
        <ContainerApplyValue>
          <SelectField
            name={'selectAll'}
            value={valueOfAll}
            defaultOption={true}
            options={hour}
            handlechange={(e: any) => {
              setValueOfAll(Number(e.target.value));
            }}
            required={false}
            label={'Valeur à appliquer à la séléction'}
          />
          <Button
            sx={{ marginTop: '6px', marginLeft: '16px' }}
            id="valueOfAllButton"
            textButton="Valider"
            onClick={() => applyValue(valueOfAll === undefined ? 0 : valueOfAll)}
          />
        </ContainerApplyValue>
      )}
      <StyledTable>
        <BasicTable
          columns={[
            <SelectAll key="selectAll" />,
            'Jour',
            'Date',
            'Temps de travail',
            'Heures supp (HNO)',
            'Commentaire',
          ].filter((item, index) => (readOnly ? index !== 0 : true))}
        >
          {orderedDays.map((row, i) => (
            <TableRow key={`eachDay-${i}`}>
              {!readOnly && (
                <TableCellBody id={`dayWorkedCheckBox-${i}`}>
                  <Checkbox
                    id={`dayWorkedCheckBoxInput-${i}`}
                    name={`dayWorkedCheckBox${i}`}
                    checked={row.checked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>, check: boolean) => {
                      setShowApplyValue(true);
                      handleWorkedDay(row.id, check);
                    }}
                    disabled={false}
                    label={''}
                  />
                </TableCellBody>
              )}
              <TableCellBody id={`day-${i}`} sx={{ textAlign: 'left' }}>
                {row.day}
              </TableCellBody>
              <TableCellBody id={`holiday-${i}`}>
                {row.isHoliday ? (
                  <SingleLineText
                    sx={(theme: Theme) => ({
                      color: theme.palette.red[80],
                      fontWeight: 'bold',
                    })}
                    text={`${row.date} Férié !!`}
                  />
                ) : (
                  <SingleLineText text={row.date} />
                )}
              </TableCellBody>
              <TableCell id={`dayWorked${i}`}>
                {readOnly ? (
                  <SingleLineText
                    text={row.dayWorked?.toString() || '0'}
                    sx={{ textAlign: 'center', fontWeight: 'bold' }}
                  />
                ) : (
                  <SelectField
                    readOnly={readOnly}
                    disabled={readOnly}
                    name={`dayWorkedField${i}`}
                    id={`dayWorkedField${i}`}
                    value={row.dayWorked}
                    options={hour}
                    handlechange={(e: any) => {
                      handleHalfDay(row.id, Number(e.target.value));
                    }}
                    label={''}
                    required={false}
                  />
                )}
              </TableCell>
              <TableCellBody id={`dayAstreinteCheckBox-${i}`}>
                {readOnly ? (
                  <SingleLineText
                    text={row.astreinte ? row.astreinte.toString() : '0'}
                    sx={{ textAlign: 'center', fontWeight: 'bold' }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      flexFlow: 'row nowrap',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {!readOnly && (
                      <Checkbox
                        name={`dayAstreinteCheckBoxInput${i}`}
                        checked={showAstreinteSelect[row.id] === true}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, check: boolean) => {
                          handleShowAstreinte(row.id, check);
                        }}
                        id={`dayAstreinteCheckBox-${i}`}
                        disabled={false}
                        label={''}
                      />
                    )}
                    {showAstreinteSelect[row.id] === true && (
                      <SelectField
                        readOnly={readOnly}
                        disabled={readOnly}
                        name={`dayAstreinteSelectInput${i}`}
                        id={`dayAstreinteSelectInput${i}`}
                        value={row.astreinte || 0}
                        options={hourHNO}
                        handlechange={(e: any) => {
                          handleAstreinte(row.id, Number(e.target.value));
                        }}
                        label={''}
                        required={false}
                      />
                    )}
                  </Box>
                )}
              </TableCellBody>
              <TableCell id={`comment-${i}`}>
                {readOnly ? (
                  <SingleLineText text={row?.comment || ''} sx={{ fontSize: '0.875rem', color: 'black' }} />
                ) : (
                  <>
                    <ValidationTextField
                      disabled={readOnly}
                      readOnly={readOnly}
                      type={TypeInput.text}
                      id={`commentField-${i}`}
                      value={comments[row.id] || ''}
                      maxLength={'50'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        handleComment(row.id, e.target.value);
                      }}
                      label={'Commentaire'}
                      placeholder={'Commentaire ...'}
                    />
                    {!readOnly && (
                      <SingleLineText
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: '-1em',
                          paddingRight: ' 0.5em',
                        }}
                        text={`${(comments[row.id] || '').length}/50`}
                      />
                    )}
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </BasicTable>
      </StyledTable>
    </>
  ) as React.ReactNode;
};

const TableCellBody = styled(TableCell)(() => ({
  textAlign: 'center' as CanvasTextAlign,
}));

const StyledTable = styled('div')(({ theme }) => ({
  margin: 'auto',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.secondary.main,
    '& .MuiTableCell-head': {
      color: 'white',
      fontWeight: 'bold',
      fontSize: '0.875rem',
      padding: theme.spacing(1.5),
      border: 'none',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.grey[50],
      },
      '&:nth-of-type(odd)': {
        backgroundColor: 'white',
      },
      '& .MuiTableCell-body': {
        padding: theme.spacing(1),
        border: 'none',
        fontSize: '0.875rem',
        color: 'black',
      },
    },
  },
}));

const ContainerApplyValue = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  justifyContent: 'space-between',
  width: '700px',
  margin: '1em auto',
  alignItems: 'flex-start',
  alignContent: 'center',
}));
